import * as React from 'react';
import styled, { keyframes } from 'styled-components';

const pulse = keyframes`
    0% {
        opacity: 0.6;
    }
    50% {
        opacity: 1;
    }
    100% {
        opacity: 0.6;
    }
`;

const SkeletonWrapper = styled.div`
    border-radius: 10px;
    border: 1px solid #e8ecf2;
    overflow: hidden;
    background: #fff;
`;

const HeaderRow = styled.div`
    display: flex;
    background: #ede8da;
    padding: 13px 20px;
    border-bottom: 1px solid #d9d3c2;
    gap: 16px;
`;

const HeaderCell = styled.div`
    flex: 1;
    height: 11px;
    border-radius: 4px;
    background: linear-gradient(90deg, #d9d3c2 0%, #ccc6b6 50%, #d9d3c2 100%);
    animation: ${pulse} 1.4s ease-in-out infinite;
    max-width: 110px;
`;

const Row = styled.div`
    display: flex;
    padding: 18px 20px;
    border-bottom: 1px solid #f2f4f7;
    gap: 16px;

    &:last-child {
        border-bottom: none;
    }
`;

const Cell = styled.div<{ $width: string }>`
    flex: 1;
    max-width: ${({ $width }) => $width};
    height: 14px;
    border-radius: 4px;
    background: linear-gradient(90deg, #f0f2f5 0%, #e4e8ed 50%, #f0f2f5 100%);
    background-size: 200% 100%;
    animation: ${pulse} 1.6s ease-in-out infinite;
`;

interface Props {
    rows?: number;
    columns?: number;
}

/* Skeleton loader for PaginatedTable. Renders header + N row placeholders
   that pulse softly. Used while data is fetching for the first time so
   the user sees the shape of the eventual content instead of a blank
   spinner — about 2x faster perceived load. */
export const TableSkeleton: React.FC<Props> = ({ rows = 5, columns = 5 }) => {
    /* Vary the cell widths a bit so the skeleton doesn't look like
       a chequerboard — gives it a content-shaped feel. */
    const widths = ['60%', '40%', '30%', '50%', '35%', '45%', '55%'];

    return (
        <SkeletonWrapper>
            <HeaderRow>
                {Array.from({ length: columns }).map((_, idx) => (
                    <HeaderCell key={`h-${idx}`} />
                ))}
            </HeaderRow>
            {Array.from({ length: rows }).map((_, rowIdx) => (
                <Row key={`r-${rowIdx}`}>
                    {Array.from({ length: columns }).map((_, colIdx) => (
                        <Cell
                            key={`c-${rowIdx}-${colIdx}`}
                            $width={widths[colIdx % widths.length]}
                        />
                    ))}
                </Row>
            ))}
        </SkeletonWrapper>
    );
};
